import { uniq } from 'lodash';
import React, { useState } from 'react';
import {
	Form,
	InputGroup,
	Collapse,
	Dropdown,
	ListGroup,
} from 'react-bootstrap';
import { BsQuestionCircle } from 'react-icons/bs';

import {
	BayModal,
	BaySelect,
	BaySpinner,
	BayTooltip,
	BayAddButton,
	BayCheckbox,
} from '../..';

// TODO: Create proper types for template data structures
interface Category {
	_id: string;
	name?: string;
	label?: string;
	owner?: any;
}

interface Tag {
	_id: string;
	name?: string;
	label?: string;
	value?: string;
	tagSet?: any;
}

interface NormalizedTagOption extends Tag {
	label: string;
	value: string;
}

interface Customer {
	_id: string;
	name?: string;
	displayName?: string;
	label?: string;
	idNumber?: string;
}

interface Access {
	isGlobalRole?: boolean;
	[key: string]: any;
}

interface SaveModalProps {
	// Modal state
	show: boolean;
	onHide: () => void;

	// Template data
	templateOwner?: string | null;
	templateStatus?: any; // TODO: Create proper type
	templateCategories?: Category[] | null;
	templateTags?: Tag[];

	// Form options
	customers?: Customer[];
	formCategories?: Category[];
	tagOptions?: any[];
	tagSetOptions?: any[];
	statusOptionsArray?: any[];

	// Form handlers
	onOwnerChange?: (value: string | null, flag?: boolean) => void;
	onStatusChange?: (value: string | null) => void;
	onCategoryChange?: (category: Category) => void;
	onTagsChange?: (tags: Tag[]) => void;
	onCreateTag?: (data: { tagSet: any; tagName: string; ownerId: string | null }) => Promise<any>;

	// Save handlers
	onSave: (e: React.FormEvent) => Promise<void>;

	// State flags
	isSaving?: boolean;
	isCategoriesLoading?: boolean;

	// Access control
	access?: Access;

	// UI customization
	hideCategories?: boolean;
	/**
	 * Reusable-block editor variant: "Save Block" title, a single step (blocks
	 * carry no general tags) and a single-select Block Category instead of the
	 * multi-select checklist.
	 */
	blockMode?: boolean;

	// Translation
	t: (key: string, options?: any) => string;
}

const toDisplayText = (value: any): string => {
	if (typeof value === 'string' || typeof value === 'number') {
		return String(value);
	}

	if (!value || typeof value !== 'object') {
		return '';
	}

	const nextValue = value.displayName || value.name || value.label || '';
	if (typeof nextValue === 'string' || typeof nextValue === 'number') {
		return String(nextValue);
	}

	return '';
};

const normalizeTagOption = (
	tag: Tag | null | undefined,
): NormalizedTagOption | null => {
	if (!tag) return null;

	const tagSetName = toDisplayText(tag?.tagSet?.name);
	const tagName = toDisplayText(tag?.name);
	const fallbackLabel = [tagSetName, tagName].filter(Boolean).join(':');
	const label =
		toDisplayText(tag?.label) || fallbackLabel || toDisplayText(tag?._id);
	const value =
		tag?.value !== undefined && tag?.value !== null && tag?.value !== ''
			? tag.value
			: tag?._id;

	if (value === undefined || value === null || value === '') return null;

	return {
		...tag,
		label,
		value,
	};
};

interface CategoriesListProps {
	categories?: Category[];
	selectedCategories?: Category[];
	onSelectCategory: (category: Category) => void;
	isSaving?: boolean;
}

const CategoriesList: React.FC<CategoriesListProps> = ({
	categories,
	selectedCategories,
	onSelectCategory,
	isSaving,
}) => {
	return (
		<ListGroup className="category-list-items" variant="flush">
			{categories?.map((category, index) => {
				const isSelected = selectedCategories?.some(
					(selected) => selected?._id === category?._id,
				);
				const ownerText = toDisplayText(category?.owner);
				const categoryText = toDisplayText(category?.name || category?.label);
				return (
					<ListGroup.Item
						key={index}
						action
						active={Boolean(isSelected)}
						as="div"
						className="d-flex align-items-center p-1 px-2 cursor-pointer category-list-item"
						disabled={isSaving}
						onClick={() => onSelectCategory(category)}
					>
						<BayCheckbox
							isChecked={Boolean(isSelected)}
							name={`category_${index}`}
							onChangeFunc={() => onSelectCategory(category)}
						/>

						<div className="ms-2 flex-1 text-truncate">
							<span className="category-label">{categoryText}</span>
						</div>

						<div className="text-700 ms-auto text-truncate">
							<span className="owner-label">{ownerText}</span>
						</div>
					</ListGroup.Item>
				);
			})}
		</ListGroup>
	);
};

const CustomOption: React.FC<any> = (data) => (
	<div>
		<span className="fs-6 fw-semibold">
			{toDisplayText(data?.name || data?.label)}
		</span>{' '}
		<br />
		<span className="fs-7">
			ID Number: {toDisplayText(data?.idNumber) || 'No ID Number'}
		</span>
	</div>
);

interface CustomSaveTemplateModalFooterProps {
	handleContinue: (e: React.MouseEvent<HTMLButtonElement>, isCancelButton?: boolean) => void;
	isDisabled: boolean;
	isSaving: boolean;
	isSuperAdmin: boolean;
	saveModalStep: number;
	hideCategories: boolean;
	t: (key: string) => string;
	handleCancelButton?: () => void;
}

const CustomSaveTemplateModalFooter: React.FC<CustomSaveTemplateModalFooterProps> = ({
	handleContinue,
	isDisabled,
	isSaving,
	isSuperAdmin,
	saveModalStep,
	hideCategories,
	t,
}) => {
	const getContinueButtonText = () => {
		if (isSaving) return t('saving');
		if (saveModalStep === 1) {
			return hideCategories || !isSuperAdmin ? t('save') : t('continue');
		}
		return t('save');
	};

	const getCancelButtonText = () => {
		if (saveModalStep === 1) return t('cancel');
		return t('back');
	};

	return (
		<>
			<button
				className="btn btn-outline-secondary"
				disabled={isSaving}
				type="button"
				onClick={(e) => handleContinue(e, true)}
			>
				{getCancelButtonText()}
			</button>
			<div className="d-flex justify-content-end">
				<button
					className="btn btn-primary"
					disabled={isDisabled}
					type="button"
					onClick={handleContinue}
				>
					{getContinueButtonText()}
				</button>
			</div>
		</>
	);
};

const SaveModal: React.FC<SaveModalProps> = ({
	// Modal state
	show,
	onHide,

	// Template data
	templateOwner,
	templateStatus,
	templateCategories,
	templateTags = [],

	// Form options
	customers = [],
	formCategories = [],
	tagOptions = [],
	tagSetOptions = [],
	statusOptionsArray = [],

	// Form handlers
	onOwnerChange = () => {},
	onStatusChange = () => {},
	onCategoryChange = () => {},
	onTagsChange = () => {},
	onCreateTag,

	// Save handlers
	onSave,

	// State flags
	isSaving = false,
	isCategoriesLoading = false,

	// Access control
	access = {},

	// UI customization
	hideCategories = false,

	// Reusable-block editor variant: "Save Block" title, single-step (no tags),
	// and a single-select Block Category (one category per block).
	blockMode = false,

	// Translation
	t,
}) => {
	const [tagValue, setTagValue] = useState('');
	const [tagSetOption, setTagSetOption] = useState<any>('');
	const [showTagCreation, setShowTagCreation] = useState(false);
	const menuPortalTarget =
		typeof document !== 'undefined' ? document.body : undefined;
	const selectMenuPortalStyles = {
		menuPortal: (base: any) => ({
			...base,
			// eslint-disable-next-line no-restricted-syntax -- grandfathered: raw z-index fenced as step 2b, the portal band is a cross-repo decision. See docs/css-unification/overlay-and-zindex.md
			zIndex: 1080,
		}),
	};

	// Modal step control - managed internally
	const [saveModalStep, setSaveModalStep] = useState(1);

	const resetModalState = () => {
		setTagValue('');
		setTagSetOption('');
		setShowTagCreation(false);
		setSaveModalStep(1);
	};

	/**
	 * A block MUST carry a Block Category — it is how the library groups and
	 * filters blocks, so an uncategorised block is unreachable there.
	 *
	 * Enforced in two places on purpose: the button is disabled so the rule is
	 * visible before the click, and handleContinue refuses anyway, because the
	 * modal can also be submitted by paths that do not go through that button.
	 */
	const isBlockCategoryMissing = blockMode && !templateCategories?.length;

	const handleContinue = (
		e: React.MouseEvent<HTMLButtonElement>,
		isCancelButton: boolean = false,
	) => {
		e.preventDefault();

		switch (isCancelButton) {
		case true:
			if (saveModalStep === 1) {
				handleCancelButton();
			} else {
				setSaveModalStep(1);
			}
			break;
		default:
			// A block without a category cannot be saved at all.
			if (isBlockCategoryMissing) {
				return;
			}
			// If hideCategories is true (email templates), always save immediately.
			// Blocks have no tags step, so they also save immediately.
			// Non-global admins should also save immediately (no step 2)
			if (blockMode || hideCategories || !access?.isGlobalRole) {
				handleSaveButton(e as any);
			} else {
				if (saveModalStep === 2) {
					handleSaveButton(e as any);
				} else {
					setSaveModalStep(2);
				}
			}
			break;
		}
	};

	const handleCancelButton = () => {
		onHide();
		resetModalState();
	};

	const handleSaveButton = async (e: React.FormEvent) => {
		e.preventDefault();
		await onSave(e);
		onHide();
		resetModalState();
	};

	const handleFormChange = (name: string, valueObject: any) => {
		switch (name) {
		case 'template_owner':
			onOwnerChange(valueObject?.value ?? null, true);
			break;
		case 'template_status':
			onStatusChange(valueObject?.value ?? null);
			break;
		case 'template_category':
			onCategoryChange(valueObject);
			break;
		case 'template_general_tags':
			onTagsChange(valueObject);
			break;
		default:
			break;
		}
	};

	const handleTagAdd = async () => {
		if (!tagValue || !tagSetOption || !onCreateTag) return;

		const newTag = await onCreateTag({
			tagSet: tagSetOption,
			tagName: tagValue,
			ownerId: templateOwner || null,
		});

		if (newTag) {
			const uniqueTags = uniq(
				[
					...templateTags,
					{
						...newTag,
						tagSet: tagSetOption,
						label: `${tagSetOption?.label || 'Unknown'}:${newTag?.name || 'Unknown'}`,
						value: newTag?._id,
					},
				],
				'_id',
			);

			onTagsChange(uniqueTags as any);
			setTagValue('');
		}
	};

	const getDefaultOwnerValue = (customers: Customer[], selectedOwner: string | null | undefined) => {
		if (
			selectedOwner === undefined ||
			selectedOwner === null ||
			selectedOwner === '' ||
			customers?.length === 0
		) {
			return null;
		}

		const currentOwner = customers.find(
			(e) => String(e?._id) === String(selectedOwner),
		);

		return currentOwner
			? {
				...currentOwner,
				label: toDisplayText(currentOwner.name) || 'Unknown',
				value: currentOwner._id,
			}
			: null;
	};

	const getOwnerOptions = (customers: Customer[]) => {
		return customers?.map((e) => ({
			...e,
			label: toDisplayText(e.name) || 'Unknown',
			value: e._id,
		}));
	};

	// Block Category is a single-select (one category per block). Map the same
	// category list the multi-select checklist uses into BaySelect options, and
	// resolve the currently-selected category (templateCategories holds at most
	// one entry in block mode).
	const getBlockCategoryOptions = (categories?: Category[]) => {
		return (categories || []).map((category) => ({
			...category,
			label: toDisplayText(category?.name || category?.label) || 'Unknown',
			value: category?._id,
		}));
	};

	const getSelectedBlockCategoryValue = (
		categories?: Category[],
		selectedCategories?: Category[],
	) => {
		const selected = (selectedCategories || [])[0];
		if (!selected) return null;

		const match = (categories || []).find(
			(category) => String(category?._id) === String(selected?._id),
		);
		const source = match || selected;

		return {
			...source,
			label: toDisplayText(source?.name || source?.label) || 'Unknown',
			value: source?._id,
		};
	};

	const normalizedTagOptions = (tagOptions || [])
		.map(normalizeTagOption)
		.filter((tag): tag is NormalizedTagOption => Boolean(tag));
	const normalizedTemplateTags = (templateTags || [])
		.map(normalizeTagOption)
		.filter((tag): tag is NormalizedTagOption => Boolean(tag));

	return (
		<BayModal
			backdrop="static"
			closeButton={!isSaving}
			footer={
				<CustomSaveTemplateModalFooter
					handleCancelButton={handleCancelButton}
					handleContinue={handleContinue}
					hideCategories={hideCategories}
					isDisabled={
						isSaving ||
						isCategoriesLoading ||
						isBlockCategoryMissing ||
						(access.isGlobalRole &&
							(templateOwner === null || templateOwner === ''))
					}
					isSaving={isSaving}
					isSuperAdmin={access?.isGlobalRole || false}
					saveModalStep={saveModalStep}
					t={t}
				/>
			}
			height="100%"
			id="save_template_modal"
			show={show}
			title={blockMode ? t('saveBlockTitle') : t('saveTemplateTitle')}
			translate={t}
			onHideFunc={onHide}
		>
			{isCategoriesLoading ? (
				<div
					className="d-flex justify-content-center"
					style={{ minHeight: 220 }}
				>
					<BaySpinner />
				</div>
			) : (
				<>
					<p>{blockMode ? t('saveBlockContent') : t('saveTemplateContent')}</p>

					<Form
						className={`scrollbar-sm pe-3 ${
							formCategories.length <= 0 || saveModalStep === 2
								? 'overflow-y-visible'
								: ''
						}`}
						id="save_template_form"
						onSubmit={(e) => e.preventDefault()}
					>
						{saveModalStep === 1 && (
							<>
								{access?.isGlobalRole && (
									<Form.Group className="py-2">
										<Form.Label aria-required htmlFor="templateOwner">
											{t('owner')}
										</Form.Label>
										<BaySelect
											customStyles={selectMenuPortalStyles}
											customTemplate={CustomOption}
											controlledValue={getDefaultOwnerValue(
												customers,
												templateOwner,
											)}
											disabled={isSaving || customers?.length === 0}
											id="templateOwner"
											menuPortalTarget={menuPortalTarget}
											menuPosition="fixed"
											menuShouldScrollIntoView={false}
											name="template_owner"
											optionsArray={getOwnerOptions(customers)}
											onChangeFunc={(value: any) =>
												handleFormChange('template_owner', value)
											}
										/>
										<Form.Text muted className="d-block mt-2 ps-1">
											{t('additionalOwnersHint')}
										</Form.Text>
										{(templateOwner === null || templateOwner === '') && (
											<div className="invalid-feedback d-block">
												{t('required')}
											</div>
										)}
									</Form.Group>
								)}

								<Form.Group className="py-2">
									<Form.Label>{t('templateStatus')}</Form.Label>
									<BaySelect
										controlledValue={templateStatus}
										customStyles={selectMenuPortalStyles}
										disabled={isSaving}
										isClearable={false}
										menuPortalTarget={menuPortalTarget}
										menuPosition="fixed"
										menuShouldScrollIntoView={false}
										name="template_status"
										optionsArray={statusOptionsArray}
										onChangeFunc={(value: any) =>
											handleFormChange('template_status', value)
										}
									/>
								</Form.Group>

								{/* Blocks: a single Block Category per block (Highlander rule),
								   shown as a select rather than the multi-select checklist. */}
								{blockMode && formCategories?.length > 0 && (
									<Form.Group className="py-2 mb-2" id="block_category">
										<Form.Label aria-required htmlFor="template_category">
											{t('blockCategory')}
											<span className="text-danger ms-1">{t('required')}</span>
										</Form.Label>
										<BaySelect
											customStyles={selectMenuPortalStyles}
											controlledValue={getSelectedBlockCategoryValue(
												formCategories,
												templateCategories,
											)}
											disabled={isSaving}
											isClearable
											menuPortalTarget={menuPortalTarget}
											menuPosition="fixed"
											menuShouldScrollIntoView={false}
											name="template_category"
											optionsArray={getBlockCategoryOptions(formCategories)}
											onChangeFunc={(value) =>
												handleFormChange('template_category', value)
											}
										/>
									</Form.Group>
								)}

								{!blockMode &&
									!hideCategories &&
									((templateOwner !== null && templateOwner !== '') ||
										(!access.isGlobalRole &&
											(templateOwner === null || templateOwner === ''))) &&
									formCategories?.length > 0 && (
									<Form.Group
										className="py-2 mb-2"
										disabled={isSaving}
										id="categories_list"
									>
										<div
											className="d-flex align-items-center justify-content-between border-bottom mb-2"
											id="categories_list_header"
										>
											<Form.Label>{t('allCategories')}</Form.Label>
											<Form.Label>{t('categoryOwner')}</Form.Label>
										</div>

										{formCategories?.length > 0 && (
											<CategoriesList
												categories={formCategories}
												isSaving={isSaving}
												selectedCategories={templateCategories || []}
												onSelectCategory={(category) =>
													handleFormChange('template_category', category)
												}
											/>
										)}
									</Form.Group>
								)}
							</>
						)}

						{access?.isGlobalRole && saveModalStep === 2 && (
							<>
								<Form.Group className="py-2">
									<Form.Label>
										<span className="me-2">{t('generalTags')}</span>
										<BayTooltip
											overlay={t('generalTagsOverlay')}
											placement="top"
										>
											<BsQuestionCircle />
										</BayTooltip>
									</Form.Label>

									<BaySelect
										isClearable
										allowSearch={false}
										allowSelectAll={false}
										classNamePrefix="bay-tags-select"
										closeMenuOnSelect={false}
										createable={false}
										controlledValue={normalizedTemplateTags}
										disabled={isSaving || normalizedTagOptions.length === 0}
										hideSelectedOptions
										isMulti
										name="template_general_tags"
										optionsArray={normalizedTagOptions}
										placeholder="Select..."
										tabSelectsValue={false}
										onChangeFunc={(value: any) =>
											handleFormChange('template_general_tags', value)
										}
									/>
									<Form.Text muted className="d-block mt-2 ps-1">
										{t('additionalTagsHint')}
									</Form.Text>
								</Form.Group>

								<Collapse in={showTagCreation}>
									<Form.Group className="py-2">
										<Form.Label>
											{t('create')} {t('tags')}
										</Form.Label>
										<InputGroup>
											<Dropdown>
												<Dropdown.Toggle
													className="border"
													disabled={isSaving}
													id="tagset_dropdown"
													variant="light"
												>
													{tagSetOption?.label || 'Select'}
												</Dropdown.Toggle>
												<Dropdown.Menu
													className="scrollbar-sm"
													id="tagset_dropdown_menu"
												>
													{tagSetOptions?.map((tagSet, index) => (
														<Dropdown.Item
															key={index}
															onClick={() => setTagSetOption(tagSet)}
														>
															{tagSet.label || 'Unknown'}
														</Dropdown.Item>
													))}
												</Dropdown.Menu>
											</Dropdown>
											<Form.Control
												disabled={isSaving}
												id="templateTagsInput"
												value={tagValue}
												onChange={(e) => setTagValue(e.target.value)}
											/>
											<BayAddButton
												className="border fw-normal"
												disabled={
													tagValue === '' || tagSetOption === '' || isSaving
												}
												text={t('create')}
												variant="outline-primary"
												onClick={handleTagAdd}
											/>
										</InputGroup>
									</Form.Group>
								</Collapse>

								<BayAddButton
									className="mt-2 p-1"
									disabled={isSaving}
									showIcon={!showTagCreation}
									size="sm"
									text={
										!showTagCreation
											? `${t('create')} ${t('tags')}`
											: 'Hide Input'
									}
									variant="link"
									onClick={() => setShowTagCreation(!showTagCreation)}
								/>
							</>
						)}
					</Form>
				</>
			)}
		</BayModal>
	);
};

export default SaveModal;
export { CategoriesList };
